import { defineStore } from "pinia";
import { getFeatureServicesInfo } from "@/api/getInfo";
import {
  createPersistenceConfig,
  readCache,
  writeCache,
  clearCache,
  isCacheAvailable,
  isCacheExpiring,
  getCacheInfo,
  type CacheInfo,
} from "@/utils/cache";

// ========== 持久化配置 ==========
const PERSISTENCE_CONFIG = createPersistenceConfig("featuredService");

interface FeatureServiceType {
  id: number;
  name: string;
  icon: string;
  info: string;
}

interface FeatureServiceState {
  featureServices: FeatureServiceType[];
}

export const useFeatureServiceStore = defineStore("featuredService", {
  state: (): FeatureServiceState => ({
    featureServices: [],
  }),
  getters: {
    getFeatureServicesInfo: (state) => {
      return state.featureServices;
    },
  },
  actions: {
    async fetchFeatureServices(
      forceRefresh = false,
      silentRefresh = false,
    ): Promise<void> {
      // 1. 检查内存缓存
      if (this.featureServices.length > 0 && !forceRefresh) {
        return;
      }
      // 2. 检查 localStorage 缓存
      if (!forceRefresh) {
        const cachedData = readCache<FeatureServiceType[]>(PERSISTENCE_CONFIG);

        if (cachedData && Array.isArray(cachedData)) {
          this.featureServices = cachedData;

          // 静默刷新：缓存即将过期时后台异步刷新
          if (silentRefresh && isCacheExpiring(PERSISTENCE_CONFIG)) {
            this.refreshInBackground();
          }

          return;
        }
      }

      // 3. 请求接口
      try {
        const featureServices = await getFeatureServicesInfo();
        const data = featureServices.data;

        if (!Array.isArray(data)) {
          throw new Error("接口返回数据格式异常");
        }

        this.featureServices = data;
        writeCache(PERSISTENCE_CONFIG, data);
      } catch (error) {
        // 降级策略
        if (isCacheAvailable(PERSISTENCE_CONFIG)) {
          const fallbackData =
            readCache<FeatureServiceType[]>(PERSISTENCE_CONFIG);
          if (fallbackData && Array.isArray(fallbackData)) {
            this.featureServices = fallbackData;
            return;
          }
        }

        if (this.featureServices.length === 0) {
          throw error;
        }
      }
    },
    async refreshInBackground(): Promise<void> {
      try {
        const featureServices = await getFeatureServicesInfo();
        const data = featureServices.data;

        if (Array.isArray(data)) {
          this.featureServices = data;
          writeCache(PERSISTENCE_CONFIG, data);
        }
      } catch (error) {}
    },

    clearCache(): void {
      this.featureServices = [];
      clearCache(PERSISTENCE_CONFIG);
    },

    getCacheInfo(): CacheInfo {
      return getCacheInfo(PERSISTENCE_CONFIG);
    },
  },
});
